import { Title, Stack, TextInput, Textarea, Switch, Select, TagsInput, Radio, Group, Button, Card, Text, Code } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rulesApi } from "../api/rules.js";
import { testRuleApi } from "../api/testRule.js";
import { notifications } from "@mantine/notifications";
import type { Rule, MatchKind, CombineWith } from "@cas/shared";
import { useState } from "react";

const MATCH_OPTIONS = [
  { value: "exact",    label: "exact" },
  { value: "contains", label: "contains" },
  { value: "domain",   label: "domain" },
  { value: "regex",    label: "regex" },
];

interface FormValues {
  name: string;
  enabled: boolean;
  senderMatch: MatchKind | "";
  senderValue: string;
  subjectMatch: MatchKind | "";
  subjectValues: string[];
  bodyMatch: MatchKind | "";
  bodyValues: string[];
  combineWith: CombineWith;
  forwardToSupport: boolean;
  markAsRead: boolean;
  overrideFolderId: string;
  notes: string;
}

const empty: FormValues = {
  name: "", enabled: true,
  senderMatch: "", senderValue: "",
  subjectMatch: "", subjectValues: [],
  bodyMatch: "", bodyValues: [],
  combineWith: "AND",
  forwardToSupport: false, markAsRead: false,
  overrideFolderId: "", notes: "",
};

function fromRule(r: Rule): FormValues {
  return {
    name: r.name, enabled: r.enabled,
    senderMatch:  r.senderMatch ?? "",
    senderValue:  r.senderValue ?? "",
    subjectMatch: r.subjectMatch ?? "",
    subjectValues: r.subjectValues ?? [],
    bodyMatch:    r.bodyMatch ?? "",
    bodyValues:   r.bodyValues ?? [],
    combineWith: r.combineWith,
    forwardToSupport: r.forwardToSupport,
    markAsRead: r.markAsRead,
    overrideFolderId: r.overrideFolderId ?? "",
    notes: r.notes ?? "",
  };
}

function toRule(v: FormValues, original?: Rule): Rule {
  return {
    id: original?.id ?? "(new)",
    name: v.name, enabled: v.enabled,
    senderMatch:  v.senderMatch || null,
    senderValue:  v.senderMatch ? v.senderValue : null,
    subjectMatch: v.subjectMatch || null,
    subjectValues: v.subjectMatch ? v.subjectValues : null,
    bodyMatch:    v.bodyMatch || null,
    bodyValues:   v.bodyMatch ? v.bodyValues : null,
    combineWith: v.combineWith,
    forwardToSupport: v.forwardToSupport,
    markAsRead: v.markAsRead,
    overrideFolderId: v.overrideFolderId || null,
    notes: v.notes || null,
    createdAt: original?.createdAt ?? "",
    createdBy: original?.createdBy ?? "",
    modifiedAt: original?.modifiedAt ?? "",
    modifiedBy: original?.modifiedBy ?? "",
  };
}

export function RuleEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const isNew = !id || id === "new";

  const { data: existing } = useQuery({
    queryKey: ["rules", id],
    queryFn: () => rulesApi.list().then(rs => rs.find(r => r.id === id)),
    enabled: !isNew,
  });

  const form = useForm<FormValues>({ initialValues: existing ? fromRule(existing) : empty });

  const save = useMutation({
    mutationFn: async (v: FormValues) => {
      const r = toRule(v, existing);
      if (isNew) return rulesApi.create(r);
      return rulesApi.update(existing!.id, r);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rules"] });
      notifications.show({ title: "Saved", message: "Rule saved", color: "green" });
      nav("/rules");
    },
    onError: (e: any) => notifications.show({ title: "Save failed", message: String(e.body?.errors ?? e), color: "red" }),
  });

  const [testEmail, setTestEmail] = useState({ from: "", subject: "", body: "" });
  const [testResult, setTestResult] = useState<string>("");
  const test = useMutation({
    mutationFn: () => testRuleApi.run(toRule(form.values, existing), testEmail),
    onSuccess: (r) => setTestResult(r.matched ? "✅ MATCHED" : "❌ no match"),
    onError: (e) => setTestResult(`error: ${String(e)}`),
  });

  return (
    <Stack maw={720} mx="auto">
      <Title order={2}>{isNew ? "New rule" : `Edit: ${existing?.name ?? "..."}`}</Title>

      <Card withBorder>
        <Stack>
          <TextInput label="Name" required {...form.getInputProps("name")} />
          <Switch label="Enabled" checked={form.values.enabled} onChange={(e) => form.setFieldValue("enabled", e.currentTarget.checked)} />

          <Group grow align="flex-start">
            <Select label="Sender match" data={MATCH_OPTIONS} clearable {...form.getInputProps("senderMatch")} />
            <TextInput label="Sender value" disabled={!form.values.senderMatch} {...form.getInputProps("senderValue")} />
          </Group>

          <Group grow align="flex-start">
            <Select label="Subject match" data={MATCH_OPTIONS} clearable {...form.getInputProps("subjectMatch")} />
            <TagsInput label="Subject values (all must match — AND)" disabled={!form.values.subjectMatch} {...form.getInputProps("subjectValues")} />
          </Group>

          <Group grow align="flex-start">
            <Select label="Body match" data={MATCH_OPTIONS} clearable {...form.getInputProps("bodyMatch")} />
            <TagsInput label="Body values (all must match — AND)" disabled={!form.values.bodyMatch} {...form.getInputProps("bodyValues")} />
          </Group>

          <Radio.Group label="When multiple condition groups exist, combine with"
                       value={form.values.combineWith}
                       onChange={(v) => form.setFieldValue("combineWith", v as CombineWith)}>
            <Group><Radio value="AND" label="AND" /><Radio value="OR" label="OR" /></Group>
          </Radio.Group>

          <Group>
            <Switch label="Forward to support" checked={form.values.forwardToSupport}
                    onChange={(e) => form.setFieldValue("forwardToSupport", e.currentTarget.checked)} />
            <Switch label="Mark as read" checked={form.values.markAsRead}
                    onChange={(e) => form.setFieldValue("markAsRead", e.currentTarget.checked)} />
          </Group>

          <TextInput label="Override folder ID (optional)" placeholder="Outlook folder ID" {...form.getInputProps("overrideFolderId")} />
          <Textarea label="Notes (why does this rule exist?)" minRows={2} {...form.getInputProps("notes")} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => nav("/rules")}>Cancel</Button>
            <Button onClick={() => save.mutate(form.values)} loading={save.isPending}>
              {isNew ? "Create rule" : "Save changes"}
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack>
          <Title order={4}>Test this rule against a sample email</Title>
          <TextInput label="From" placeholder="alerts@example.com"
                     value={testEmail.from} onChange={(e) => setTestEmail({ ...testEmail, from: e.currentTarget.value })} />
          <TextInput label="Subject" value={testEmail.subject}
                     onChange={(e) => setTestEmail({ ...testEmail, subject: e.currentTarget.value })} />
          <Textarea label="Body" minRows={3} value={testEmail.body}
                    onChange={(e) => setTestEmail({ ...testEmail, body: e.currentTarget.value })} />
          <Group>
            <Button onClick={() => test.mutate()} loading={test.isPending}>Run test</Button>
            <Code>{testResult || "no result yet"}</Code>
          </Group>
          <Text size="xs" c="dimmed">
            This calls the live `/manage/test-rule` endpoint with the rule as currently shown above (whether or not you've saved).
          </Text>
        </Stack>
      </Card>
    </Stack>
  );
}
